/**
 * Step 1: Basic Information
 * 
 * Campaign basic information form:
 * - Campaign Name (required)
 * - Description (optional) 
 * - Campaign Type (required)
 * - Valid From Date (required)
 * - Expiry Date (required)
 */

import React, { useMemo } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { CampaignFormData, CampaignValidationErrors, CampaignType } from '../../../../../api/promos/types';
import '../../../../../Common/FormStyles.css';
import './CampaignTypeButtons.css';
import './StepperButtons.css';
import './StepperFormStyles.css';

interface StepBasicInfoProps {
  formData: CampaignFormData;
  validationErrors: CampaignValidationErrors;
  onChange: (updates: Partial<CampaignFormData>) => void;
  onNext: () => void;
  onCancel: () => void;
}

const StepBasicInfo: React.FC<StepBasicInfoProps> = ({
  formData,
  validationErrors,
  onChange,
  onNext,
  onCancel,
}) => {
  // Handle input changes
  const handleInputChange = (field: keyof CampaignFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.value;
    const updates: Partial<CampaignFormData> = {
      [field]: value
    };
    
    // If updating startAt, validate that endAt is not before it
    if (field === 'startAt' && value && formData.endAt) {
      if (new Date(value) > new Date(formData.endAt)) {
        // Clear endAt if it becomes invalid
        updates.endAt = '';
      }
    }
    
    onChange(updates);
  };

  // Handle expiry date change with validation
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const endDate = e.target.value;
    
    if (endDate && formData.startAt) {
      if (new Date(endDate) < new Date(formData.startAt)) {
        // Show error but still update the field
        onChange({
          endAt: endDate
        });
        return;
      }
    }
    
    onChange({
      endAt: endDate
    });
  };

  // Get minimum date for expiry date (should be after startAt)
  const getMinExpiryDate = () => {
    if (formData.startAt) {
      const startDate = new Date(formData.startAt);
      startDate.setDate(startDate.getDate() + 1); // At least 1 day after start
      return startDate.toISOString().split('T')[0];
    }
    return '';
  };

  // Check if required fields are filled to enable Next button
  const isNextDisabled = useMemo(() => {
    const hasName = Boolean(formData.name?.trim());
    const hasCampaignType = Boolean(formData.campaignType);
    const hasStartDate = Boolean(formData.startAt);
    const hasEndDate = Boolean(formData.endAt);
    
    // Check if dates are invalid (end date before start date)
    const isDateInvalid = hasStartDate && hasEndDate 
      ? new Date(formData.endAt) < new Date(formData.startAt)
      : false;
    
    return !hasName || !hasCampaignType || !hasStartDate || !hasEndDate || isDateInvalid;
  }, [formData.name, formData.campaignType, formData.startAt, formData.endAt]);

  // Handle campaign type selection
  const handleCampaignTypeChange = (type: CampaignType) => {
    onChange({
      campaignType: type
    });
  };

  return (
    <form autoComplete="off" noValidate>
      {/* Step Header */}
      <div className="mb-4">
        <h5 className="mb-2">Basic Information</h5>
        <p className="text-muted mb-0">
          Name and categorize your campaign
        </p>
      </div>

      {/* Form Fields */}
      <Row>
        {/* Campaign Name */}
        <Col lg={12} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Campaign Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              className="stepper-form-control"
              placeholder="Enter campaign name"
              value={formData.name}
              onChange={handleInputChange('name')}
              isInvalid={!!validationErrors.name}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        {/* Description */}
        <Col lg={12} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Description <span className="text-muted">(Optional)</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="stepper-form-control"
              placeholder="Brief description of campaign objectives"
              value={formData.description || ''}
              onChange={handleInputChange('description')}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
          </Form.Group>
        </Col>

        {/* Campaign Type */}
        <Col lg={12} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">
              Campaign Type <span className="text-danger">*</span>
            </Form.Label>
            <div className="campaign-type-buttons-container">
              {Object.values(CampaignType).map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`campaign-type-button ${
                    formData.campaignType === type ? 'selected' : ''
                  }`}
                  onClick={() => handleCampaignTypeChange(type)}
                >
                  {type.charAt(0) + type.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
            {validationErrors.campaignType && (
              <div className="text-danger mt-2" style={{ fontSize: '14px' }}>
                {validationErrors.campaignType}
              </div>
            )}
          </Form.Group>
        </Col>

        {/* Validity Period */}
        <Col lg={12} className="mb-4">
          <Form.Label className="stepper-form-label mb-3">
            Validity <span className="text-muted">(Campaign Duration)</span>
          </Form.Label>
          <Row>
            {/* Valid From Date */}
            <Col md={6}>
              <Form.Group className="stepper-form-group">
                <Form.Label className="stepper-form-label">
                  Valid From Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  className="stepper-form-control"
                  value={formData.startAt}
                  onChange={handleInputChange('startAt')}
                  isInvalid={!!validationErrors.startAt}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                  {validationErrors.startAt}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Expiry Date */}
            <Col md={6}>
              <Form.Group className="stepper-form-group">
                <Form.Label className="stepper-form-label">
                  Expiry Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  className="stepper-form-control"
                  value={formData.endAt}
                  onChange={handleEndDateChange}
                  min={getMinExpiryDate()}
                  isInvalid={!!validationErrors.endAt || !!(formData.endAt && formData.startAt && new Date(formData.endAt) < new Date(formData.startAt))}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                  {validationErrors.endAt || (formData.endAt && formData.startAt && new Date(formData.endAt) < new Date(formData.startAt) ? 'Expiry date must be after valid from date' : '')}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div className="stepper-buttons-container">
        {/* Back button hidden on first step */}
        <div></div>
        
        <div className="stepper-buttons-right">
          <button
            type="button"
            className="stepper-next-button"
            onClick={onNext}
            disabled={isNextDisabled}
          >
            Next
            <i className="ph-arrow-right"></i>
          </button>
        </div>
      </div>
    </form>
  );
};

export default StepBasicInfo;